// Shared tile-size control: the card slider and the fullscreen wheel-zoom
// both drive the same CSS variable.
//
// The size deliberately does NOT persist. Every visit opens fully zoomed in,
// showing the pattern at its native 1024px where the detail actually reads.
// Restoring a previous session's zoom meant a visitor who had once zoomed out
// came back to a wall of thumbnails with no clue why.

import { anchorBackgroundScale } from './background.js';

const input = document.getElementById('tile-size');
const valEl = document.getElementById('tile-size-val');
export const MIN = 128;
// The tile's native size is the ceiling: displaying it larger only upscales
// and softens, adding no detail. 1024 is the render size, but an upscaled
// background has real pixels beyond it, so the ceiling follows the image.
const DEFAULT_MAX = 1024;
let max = DEFAULT_MAX;

// The slider is stepped, so reading the size back out of it would snap each
// wheel notch away — below ~700px a 2.3% notch is smaller than half a step and
// the zoom deadlocks. Keep the real (fractional) size here instead.
let size = max;

export function getMaxTileSize() {
  return max;
}

/** Match the zoom ceiling to the displayed image's native pixel size. */
export function setMaxTileSize(px) {
  const next = Math.max(DEFAULT_MAX, Math.round(Number(px) || 0));
  if (next === max) return;
  max = next;
  input.max = String(max);
  // The clamp (an upscale compare stepping back to a 1024 original while
  // zoomed past it) is a zoom like any other: anchored to the screen centre,
  // or the viewport lurches to wherever a pan left the pattern's own centre.
  if (size > max) zoomCentered(size);
}

export function getTileSize() {
  return size;
}

// A trackpad can fire wheel events faster than the screen refreshes, so the
// DOM write is coalesced into one paint per frame.
let pendingFrame = null;

function flush() {
  pendingFrame = null;
  const rounded = Math.round(size);
  input.value = rounded;
  valEl.textContent = `${rounded} px`;
  document.body.style.setProperty('--tile-size', `${rounded}px`);
}

export function applyTileSize(px) {
  size = Math.max(MIN, Math.min(max, px));
  if (pendingFrame === null) pendingFrame = requestAnimationFrame(flush);
  return Math.round(size);
}

/** Continuous zoom, shared by the wheel, a pinch, and the slider: apply the
 *  size anchored at (cx, cy), transition-free while the gesture is live, then
 *  one settle notification. Transition-free is not an optimisation — size and
 *  position are two separate tweens, and retargeting both mid-flight on every
 *  event breaks the anchor between them, which reads as jitter the moment a
 *  pan has moved the pattern off its own centre. */
function liveZoom(px, cx, cy) {
  document.body.classList.add('wheel-zoom');
  const before = size;
  const out = applyTileSize(px);
  anchorBackgroundScale(before, size, cx, cy);
  zoomListeners.forEach((fn) => fn(out, 'move'));
  clearTimeout(settleTimer);
  settleTimer = setTimeout(() => {
    document.body.classList.remove('wheel-zoom');
    zoomListeners.forEach((fn) => fn(out, 'settle'));
  }, 350);
  return out;
}

/** Pinch entry, used by the canvas gesture: scale by `factor` under the fingers. */
export function pinchZoomBy(factor, cx, cy) {
  return liveZoom(size * factor, cx, cy);
}

/** Single-step zoom for the +/- buttons. Anchored to the screen centre: the
 *  CSS position is `50% + pan offset`, so an unanchored resize pivots around
 *  wherever a pan left the pattern's own centre — off-screen, after enough
 *  dragging. One discrete step from rest tweens coherently, so this path
 *  keeps the animation the buttons have always had. */
export function zoomCentered(px) {
  const before = size;
  const out = applyTileSize(px);
  anchorBackgroundScale(before, size, window.innerWidth / 2, window.innerHeight / 2);
  return out;
}

// The slider is a continuous gesture like the wheel, not a discrete step:
// it fires per pixel of thumb travel and needs the transition-free path.
input.oninput = () => liveZoom(Number(input.value), window.innerWidth / 2, window.innerHeight / 2);

applyTileSize(max);

// ---------- wheel zoom ----------
// Scrolling over the background zooms the tiling rather than scrolling the
// page (which had nothing to scroll, so it just rubber-banded). Panels keep
// native scrolling since they can overflow on short viewports.

const ZOOM_STEP = 1.023; // scale per wheel notch
const zoomListeners = new Set();
let settleTimer = null;

/** Subscribe to zoom activity: fn(px, phase) with phase 'move' | 'settle'. */
export function onZoom(fn) {
  zoomListeners.add(fn);
  return () => zoomListeners.delete(fn);
}

document.addEventListener(
  'wheel',
  (e) => {
    if (document.querySelector('.modal:not(.hidden)')) return; // modal owns scrolling
    const overPanel = e.target?.closest?.('.deck, .topbar, .rail, .upscale-dock, .hero');
    if (overPanel && !document.body.classList.contains('preview')) return;

    e.preventDefault();
    // Scale by the actual scroll distance rather than a fixed notch, so a
    // trackpad flick and a mouse detent both feel proportional. Clamped so a
    // single huge delta cannot jump the whole range.
    const steps = Math.max(-4, Math.min(4, -e.deltaY / 40));
    // Keep wheel zoom on the same stable centre anchor as the slider. Mouse
    // wheel input arrives as discrete detents, so anchoring each one beneath
    // the pointer also rewrites background-position in visible little jumps;
    // the resulting side-to-side movement reads as zoom jitter.
    liveZoom(size * ZOOM_STEP ** steps, window.innerWidth / 2, window.innerHeight / 2);
  },
  { passive: false }
);
